import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Download, Upload, FileText, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Generic bulk CSV import dialog used by MCQ and PYQ admin sections.
 * Props:
 *  - entity: "mcqs" | "pyq"
 *  - title: human title, e.g. "Bulk Import MCQs (CSV)"
 *  - columnsHelp: JSX/string describing required columns for user guidance
 *  - templateFilename: filename passed as download attribute
 * The component talks to:
 *   GET  /admin/{entity}/csv-template
 *   POST /admin/{entity}/bulk-import/preview
 *   POST /admin/{entity}/bulk-import
 */
export function BulkImportCsv({ entity, title, columnsHelp, templateFilename, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/admin/${entity}/csv-template`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = templateFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
    setResult(null);
  };

  const doPreview = async () => {
    if (!file) return toast.error("Please choose a CSV file first");
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/admin/${entity}/bulk-import/preview`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!file) return toast.error("Please choose a CSV file first");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/admin/${entity}/bulk-import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} record${res.data.imported === 1 ? "" : "s"}`);
        onImported?.();
      } else {
        toast.error("No rows imported — please fix the errors below");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const showErrors = result?.errors || preview?.errors || [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-3xl rounded-lg shadow-xl my-8" data-testid="bulk-import-dialog">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="font-display font-bold text-lg flex items-center gap-2">
            <Upload size={18} /> {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded hover:bg-slate-100 flex items-center justify-center"
            data-testid="bulk-import-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Step 1 – template */}
          <div className="border border-slate-200 rounded-md p-4">
            <div className="font-semibold text-sm mb-1">Step 1 — Download the CSV template</div>
            <div className="text-xs text-slate-600 mb-3">{columnsHelp}</div>
            <button
              type="button"
              onClick={downloadTemplate}
              className="h-9 px-3 rounded border border-slate-200 text-sm font-semibold hover:bg-slate-50 inline-flex items-center gap-2"
              data-testid="bulk-import-download-template"
            >
              <Download size={14} /> Download CSV Template
            </button>
          </div>

          {/* Step 2 – file */}
          <div className="border border-slate-200 rounded-md p-4">
            <div className="font-semibold text-sm mb-2">Step 2 — Upload your CSV file</div>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={onFileChange}
                className="text-sm"
                data-testid="bulk-import-file-input"
              />
              {file && (
                <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                  <FileText size={12} /> {file.name} ({Math.round(file.size / 1024)} KB)
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!file || busy}
                onClick={doPreview}
                className="h-9 px-4 rounded border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                data-testid="bulk-import-preview-btn"
              >
                {busy ? "Checking..." : "Preview & Validate"}
              </button>
              <button
                type="button"
                disabled={!file || busy || (preview && preview.valid_count === 0)}
                onClick={doImport}
                className="vr-btn-primary h-9 px-4 text-sm disabled:opacity-50"
                data-testid="bulk-import-run-btn"
              >
                {busy ? "Importing..." : "Import Now"}
              </button>
            </div>
          </div>

          {preview && !result && (
            <div className="border border-slate-200 rounded-md p-4" data-testid="bulk-import-preview-summary">
              <div className="font-semibold text-sm mb-2">Preview</div>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="px-2 py-1 rounded bg-slate-100">Total rows: <b>{preview.total_rows}</b></span>
                <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 inline-flex items-center gap-1">
                  <CheckCircle2 size={14} /> Valid: <b>{preview.valid_count}</b>
                </span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 inline-flex items-center gap-1">
                  <AlertCircle size={14} /> Invalid: <b>{preview.invalid_count}</b>
                </span>
              </div>
              {preview.valid_count > 0 && (
                <p className="text-xs text-slate-600 mt-2">
                  Only <b>{preview.valid_count}</b> valid row{preview.valid_count === 1 ? "" : "s"} will be imported. Invalid rows will be skipped.
                </p>
              )}
            </div>
          )}

          {result && (
            <div className="border border-slate-200 rounded-md p-4" data-testid="bulk-import-result">
              <div className="font-semibold text-sm mb-2">Import result</div>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="px-2 py-1 rounded bg-slate-100">Total rows: <b>{result.total_rows}</b></span>
                <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 inline-flex items-center gap-1">
                  <CheckCircle2 size={14} /> Imported: <b data-testid="bulk-import-count-imported">{result.imported}</b>
                </span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 inline-flex items-center gap-1">
                  <AlertCircle size={14} /> Failed: <b data-testid="bulk-import-count-failed">{result.failed}</b>
                </span>
              </div>
            </div>
          )}

          {showErrors.length > 0 && (
            <div className="border border-red-200 rounded-md p-4 bg-red-50/40" data-testid="bulk-import-errors">
              <div className="font-semibold text-sm mb-2 text-red-800">
                Invalid rows ({showErrors.length})
              </div>
              <div className="max-h-60 overflow-y-auto text-sm">
                <table className="w-full">
                  <thead className="text-xs text-slate-600">
                    <tr>
                      <th className="text-left p-1 w-14">Row</th>
                      <th className="text-left p-1">Question</th>
                      <th className="text-left p-1">Problem(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showErrors.map((e, i) => (
                      <tr key={i} className="border-t border-red-100 align-top">
                        <td className="p-1 font-mono">{e.row}</td>
                        <td className="p-1 truncate max-w-[200px]" title={e.question}>{e.question || <i className="text-slate-400">—</i>}</td>
                        <td className="p-1 text-red-700">
                          <ul className="list-disc pl-4">
                            {e.reasons.map((r, j) => <li key={j}>{r}</li>)}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded border border-slate-200"
            data-testid="bulk-import-done"
          >
            {result ? "Done" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
